import { Form, InputNumber, Select } from "antd";
import { useEffect, useRef } from "react";
import { callEventPopup, TOpenPopup2 } from "../../../../contexts/PopupContext";
import { callEventTrackingOp, state, useTrackingOp } from "../../../../contexts/TrackingOpContext";
import { firstLetter, formatNumber, uuid } from "../../../../util/varibles/global";
import { IDeliveryType } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import Footer from "../../../Popup/_components/Footer";
import styles from "./style.module.scss";

interface IProps {
    deliveryTypes: Partial<IDeliveryType>[]
    current_process: number
}

interface IStartProps extends IProps {
    deliveryTypeId?: string
    tankId: string
    status: number
}

interface IFinishProps extends IProps {
    availableFishAmount: number
    availableFishAmountOnVessel: number
    availableTotalWeight: number
    deliveryTypeId: string
    deliveryTypeName: string
    groupId: string
    tankId: string
    status: number
}

function StartDischargingPopup(props: IStartProps) {
    const { onClose } = callEventPopup();
    const { nextState } = callEventTrackingOp();
    const [loading] = useTrackingOp(trackingState => trackingState.loading);
    const submitted = useRef(false);
    const { deliveryTypes, deliveryTypeId, status, tankId, current_process } = props;

    useEffect(() => {
        if (submitted.current && !loading) {
            onClose();
        }
    }, [loading, onClose]);

    const handleSubmit = ({ delivery_type_id }: { delivery_type_id: string }) => {
        submitted.current = true;
        nextState({
            group_map: [{
                group_id: uuid() + `/` + Date.now(),
                tank_id: tankId,
                delivery_type_id,
            }],
            step_state: state[status],
            current_process
        });
    }

    return <Form
        initialValues={{ delivery_type_id: deliveryTypeId }}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
    >
        <p className="mb-5 text-[15px] text-comment">
            This will finish the step. Step time is logged from your action.
        </p>
        <div className="mb-8">
            <Form.Item
                className="mb-0"
                label={<span className="text-[15px]">Select delivery methods <span className="color-danger">(*)</span></span>}
                name="delivery_type_id"
                rules={[{ required: true, message: "Please select a delivery method" }]}
            >
                <Select
                    className="w-full pl-[16px]"
                    options={deliveryTypes
                        .filter(({ id }) => !!id)
                        .map(({ id, name = "" }) => ({ label: firstLetter(name.replace(/_/g, ' ')) || id, value: id }))}
                    placeholder="Select delivery methods"
                />
            </Form.Item>
        </div>
        <Footer ok={{ text: "Confirm", loading: !!loading }} cancel={{ click: onClose }} />
    </Form>
}

export const startDischarging = (props: IStartProps, openPopup: TOpenPopup2) => {
    return openPopup(<StartDischargingPopup {...props} />, {
        modalProps: {
            title: "Start discharging ?",
            width: 600,
        }
    })

}

function FinishDischargingPopup(props: IFinishProps) {
    const { onClose } = callEventPopup();
    const { nextState } = callEventTrackingOp();
    const [loading] = useTrackingOp(trackingState => trackingState.loading);
    const [form] = Form.useForm();
    const submitted = useRef(false);
    const manuallyEditedFields = useRef({ fishAmount: false, totalWeight: false });
    const {
        availableFishAmount,
        availableFishAmountOnVessel,
        availableTotalWeight,
        deliveryTypeId,
        deliveryTypeName,
        groupId,
        status,
        tankId,
        current_process
    } = props;
    const fishAmount = Number(Form.useWatch("fish_amount", form)) || 0;
    const totalWeight = Number(Form.useWatch("total_weight", form)) || 0;
    const deadFish = Number(Form.useWatch("dead_fish", form)) || 0;
    const availableDeadFishAmount = Math.max(0, availableFishAmount - fishAmount);
    const remainingFishAmount = Math.max(0, availableFishAmount - fishAmount - deadFish);
    const remainingTotalWeight = Math.max(0, availableTotalWeight - totalWeight);

    useEffect(() => {
        if (submitted.current && !loading) {
            onClose();
        }
    }, [loading, onClose]);

    useEffect(() => {
        if (deadFish > availableDeadFishAmount) {
            form.setFieldValue("dead_fish", availableDeadFishAmount);
        }
    }, [availableDeadFishAmount, deadFish, form]);

    const clampToMax = (value: string | number, max: number) => Number(value) >= max ? max : value;
    const normalizeWithMax = (value: string | number, max: number) => {
        if (Number(value) > max) {
            notify.warn(NotifyCode.W11)();
        }

        return clampToMax(value, max);
    }

    const handleFishAmountChange = (rawValue: number | null) => {
        const value = Number(rawValue) || 0;
        const clampedValue = Math.min(value, availableFishAmount);

        if (value > availableFishAmount) {
            notify.warn(NotifyCode.W11)();
        }

        form.setFieldValue("fish_amount", clampedValue > 0 ? clampedValue : undefined);
        generateTotalWeight(clampedValue);
    }

    const handleTotalWeightChange = (rawValue: number | null) => {
        const value = Number(rawValue) || 0;
        const clampedValue = Math.min(value, availableTotalWeight);

        if (value > availableTotalWeight) {
            notify.warn(NotifyCode.W11)();
        }

        form.setFieldValue("total_weight", clampedValue > 0 ? clampedValue : undefined);
        generateFishAmount(clampedValue);
    }

    const generateTotalWeight = (rawValue: string | number | null) => {
        manuallyEditedFields.current.fishAmount = true;
        if (availableFishAmount <= 0 || availableTotalWeight <= 0 || manuallyEditedFields.current.totalWeight) {
            return;
        }

        const value = clampToMax(rawValue ?? 0, availableFishAmount);
        const generatedWeight = Number(value) * availableTotalWeight / availableFishAmount;
        const roundedGeneratedWeight = Math.min(Number(generatedWeight.toFixed(3)), availableTotalWeight);
        form.setFieldValue("total_weight", roundedGeneratedWeight > 0
            ? roundedGeneratedWeight
            : undefined);
    }

    const generateFishAmount = (rawValue: string | number | null) => {
        manuallyEditedFields.current.totalWeight = true;
        if (availableFishAmount <= 0 || availableTotalWeight <= 0 || manuallyEditedFields.current.fishAmount) {
            return;
        }

        const value = clampToMax(rawValue ?? 0, availableTotalWeight);
        const generatedAmount = Number(value) >= availableTotalWeight
            ? availableFishAmount
            : Math.floor(Number(value) * availableFishAmount / availableTotalWeight);
        form.setFieldValue("fish_amount", generatedAmount > 0 ? generatedAmount : undefined);
    }

    const handleSubmit = (values: {
        dead_fish?: string | number
        fish_amount: string | number
        total_weight: string | number
    }) => {
        submitted.current = true;
        const dischargedFishAmount = Number(values.fish_amount) + Number(values.dead_fish || 0);
        nextState({
            group_map: [{
                group_id: groupId,
                tank_id: tankId,
                delivery_type_id: deliveryTypeId,
                fish_amount: Number(values.fish_amount),
                total_weight: Number(values.total_weight) * 1000000,
                ...(values.dead_fish
                    ? { dead_fish: Number(values.dead_fish) }
                    : {}),
            }],
            step_state: state[status],
            ...(dischargedFishAmount >= availableFishAmountOnVessel ? { step_new: -1 } : {}),
            current_process
        });
    }

    const positiveNumberRule = (label: string) => ({
        validator: (_: unknown, value: string | number | undefined) => value === undefined || value === "" || Number(value) >= 0
            ? Promise.resolve()
            : Promise.reject(new Error(`${label} must be greater than 0`))
    });
    const availableFishRule = ({ getFieldValue }: {
        getFieldValue: (name: string) => string | number | undefined
    }) => ({
        validator: () => {
            const fishAmount = Number(getFieldValue("fish_amount")) || 0;
            const deadFish = Number(getFieldValue("dead_fish")) || 0;
            return fishAmount + deadFish <= availableFishAmount
                ? Promise.resolve()
                : Promise.reject(new Error(`Fish count and dead fish cannot exceed ${availableFishAmount} fish`));
        }
    });

    return <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
        <p className="mb-5 text-[15px] text-comment">
            This will finish the step. Step time is logged from your action.
        </p>
        <div className="mb-8">
            <div className="mb-3 font-[600] text-[15px]">Input discharging info.</div>
            <div className="mb-3 text-[12px] font-[600] uppercase">{deliveryTypeName}</div>
            <Form.Item
                extra={<span className="text-comment text-[12px]">Remaining in tank: {formatNumber(remainingFishAmount)} fish</span>}
                label={<span className="text-[15px]">Fish count <span className="color-danger">(*)</span></span>}
                name="fish_amount"
                dependencies={["dead_fish"]}
                validateFirst
                rules={[
                    { required: true, message: "Please input the fish count" },
                    positiveNumberRule("Fish count"),
                    availableFishRule,
                ]}
            >
                <InputNumber
                    changeOnBlur={false}
                    className={`border-r-40 h-36 w-full ${styles.fishAmountInput}`}
                    formatter={(value, { input, userTyping }) => {
                        const rawValue = userTyping ? input : `${value ?? ""}`;
                        const digits = rawValue.replace(/\D/g, "");
                        if (!digits) {
                            return "";
                        }

                        return Math.min(Number(digits), availableFishAmount).toLocaleString("en-US");
                    }}
                    max={availableFishAmount}
                    min={1}
                    onChange={handleFishAmountChange}
                    onKeyDown={event => {
                        if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                            return;
                        }

                        const input = event.currentTarget;
                        const hasSelection = input.selectionStart !== input.selectionEnd;
                        const currentValue = Number(form.getFieldValue("fish_amount")) || 0;

                        if (!hasSelection && currentValue >= availableFishAmount) {
                            notify.warn(NotifyCode.W11)();
                            event.preventDefault();
                        }
                    }}
                    parser={value => Math.min(
                        Number(value?.replace(/\D/g, "") || 0),
                        availableFishAmount
                    )}
                    placeholder="Input the fish count"
                    precision={0}
                    step={1}
                    suffix={<span className="text-comment">fish</span>}
                />
            </Form.Item>
            <Form.Item
                extra={<span className="text-comment text-[12px]">Remaining in tank: {formatNumber(remainingTotalWeight)} tons</span>}
                label={<span className="text-[15px]">Total weight <span className="color-danger">(*)</span></span>}
                name="total_weight"
                normalize={value => normalizeWithMax(value, availableTotalWeight)}
                validateFirst
                rules={[
                    { required: true, message: "Please input the total weight" },
                    positiveNumberRule("Total weight"),
                    {
                        validator: (_, value) => Number(value) <= availableTotalWeight
                            ? Promise.resolve()
                            : Promise.reject(new Error(`Total weight cannot exceed ${availableTotalWeight} tons`))
                    },
                ]}
            >
                <InputNumber
                    changeOnBlur={false}
                    className={`border-r-40 h-36 w-full ${styles.fishAmountInput}`}
                    formatter={(value, { input, userTyping }) => {
                        const rawValue = (userTyping ? input : String(value ?? "")).replace(/,/g, "");
                        if (!rawValue) {
                            return "";
                        }

                        if (Number(rawValue) > availableTotalWeight) {
                            return availableTotalWeight.toLocaleString("en-US", { maximumFractionDigits: 20 });
                        }

                        const [integerPart, decimalPart] = rawValue.split(".");
                        const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");
                        return decimalPart !== undefined
                            ? `${formattedInteger}.${decimalPart}`
                            : formattedInteger;
                    }}
                    max={availableTotalWeight}
                    min={0}
                    onChange={handleTotalWeightChange}
                    onKeyDown={event => {
                        if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                            return;
                        }

                        const input = event.currentTarget;
                        const hasSelection = input.selectionStart !== input.selectionEnd;
                        const currentValue = Number(form.getFieldValue("total_weight")) || 0;

                        if (!hasSelection && currentValue >= availableTotalWeight) {
                            notify.warn(NotifyCode.W11)();
                            event.preventDefault();
                        }
                    }}
                    parser={value => Math.min(Number(value?.replace(/,/g, "") || 0), availableTotalWeight)}
                    placeholder="Input the total weight"
                    step="any"
                    suffix={<span className="text-comment">tons</span>}
                />
            </Form.Item>
            <Form.Item
                className="mb-0"
                extra={<span className="text-comment text-[12px]">Remaining in tank: {formatNumber(remainingFishAmount)} fish</span>}
                label={<span className="text-[15px]">Dead fish <span className="text-comment italic">(optional)</span></span>}
                name="dead_fish"
                dependencies={["fish_amount"]}
                normalize={value => normalizeWithMax(value, availableDeadFishAmount)}
                rules={[
                    {
                        validator: (_, value) => !value || Number(value) >= 0
                            ? Promise.resolve()
                            : Promise.reject(new Error("Dead fish must not be negative"))
                    },
                    availableFishRule,
                ]}
            >
                <InputNumber
                    changeOnBlur={false}
                    className={`border-r-40 h-36 w-full ${styles.fishAmountInput}`}
                    formatter={(value, { input, userTyping }) => {
                        const rawValue = userTyping ? input : `${value ?? ""}`;
                        const digits = rawValue.replace(/\D/g, "");
                        if (!digits) {
                            return "";
                        }

                        return Math.min(Number(digits), availableDeadFishAmount).toLocaleString("en-US");
                    }}
                    max={availableDeadFishAmount}
                    min={0}
                    onKeyDown={event => {
                        if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                            return;
                        }

                        const input = event.currentTarget;
                        const hasSelection = input.selectionStart !== input.selectionEnd;
                        const currentValue = Number(form.getFieldValue("dead_fish")) || 0;

                        if (!hasSelection && currentValue >= availableDeadFishAmount) {
                            notify.warn(NotifyCode.W11)();
                            event.preventDefault();
                        }
                    }}
                    parser={value => Math.min(
                        Number(value?.replace(/\D/g, "") || 0),
                        availableDeadFishAmount
                    )}
                    placeholder="Input dead fish"
                    step={1}
                    suffix={<span className="text-comment">fish</span>}
                />
            </Form.Item>
        </div>
        <Footer ok={{ text: "Confirm", loading: !!loading }} cancel={{ click: onClose }} />
    </Form>
}

export const finishDischarging = (props: IFinishProps, openPopup: TOpenPopup2) => {
    return openPopup(<FinishDischargingPopup {...props} />, {
        modalProps: {
            title: "Finish discharging ?",
            width: 600,
        }
    })
}
